/**
 * Leviathan Web - Login Page
 * Quasar Prime LLC
 * Tactical Industrial Authentication Interface
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Shield, Terminal } from 'lucide-react';

export default function LoginPage() {
  // Pre-filled when arriving straight from enrolment (?email=…), so someone who
  // has just set a password never has to remember which address they set it
  // for. Editable, and empty for everyone else.
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message = (err as Error).message || 'Login failed';
      setError(message);
      // "Invalid credentials" is the API's answer to both "no such account" and
      // "wrong password", and it must stay that way — telling them apart is
      // email enumeration. But it is a dead end for the person who has just
      // enrolled from an invitation and is typing the address they use day to
      // day rather than the one the invitation was issued to. So the guidance
      // lives here, on the client, where saying it costs the server nothing.
      setShowHelp(/invalid credentials/i.test(message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-dark bg-grid-pattern relative overflow-hidden p-4">
      {/* Scan line effect */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber/5 to-transparent animate-scan" />
      </div>

      {/* Corner HUD elements */}
      <div className="fixed top-4 left-4 w-24 h-24 border-l-2 border-t-2 border-amber/30 pointer-events-none" />
      <div className="fixed top-4 right-4 w-24 h-24 border-r-2 border-t-2 border-amber/30 pointer-events-none" />
      <div className="fixed bottom-4 left-4 w-24 h-24 border-l-2 border-b-2 border-amber/30 pointer-events-none" />
      <div className="fixed bottom-4 right-4 w-24 h-24 border-r-2 border-b-2 border-amber/30 pointer-events-none" />

      <div className="w-full max-w-lg relative z-10">
        {/* Login Card with aggressive geometric frame */}
        <div className="relative stat-card">
          {/* Corner brackets */}
          <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-amber" />

          {/* Outer glow frame */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber/10 via-transparent to-steel/10 opacity-50" />

          <div className="bg-panel-dark border border-panel-border shadow-panel clip-path-octagon p-10 relative">
            {/* Inner shadow for depth */}
            <div className="absolute inset-0 shadow-[inset_0_2px_12px_rgba(0,0,0,0.6)] pointer-events-none" />

            {/* Logo Section with HUD elements */}
            <div className="mb-8 text-center relative">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <div className="w-4 h-4 bg-amber rotate-45" style={{ boxShadow: '0 0 12px rgba(255,149,0,0.8)' }} />
                <h1
                  className="text-5xl font-display font-bold text-steel tracking-wider uppercase"
                  style={{
                    textShadow: '0 0 20px rgba(92, 140, 199, 0.8), 0 0 40px rgba(92, 140, 199, 0.4)',
                  }}
                >
                  LEVIATHAN
                </h1>
                <div className="w-4 h-4 bg-amber rotate-45" style={{ boxShadow: '0 0 12px rgba(255,149,0,0.8)' }} />
              </div>

              {/* Technical separator */}
              <div className="h-px bg-gradient-to-r from-transparent via-amber to-transparent mb-4" style={{ boxShadow: '0 0 8px rgba(255,149,0,0.6)' }} />

              {/* System info */}
              <div className="flex items-center justify-center space-x-4 text-xs font-mono mb-2">
                <Terminal className="w-4 h-4 text-steel" />
                <span className="text-text-secondary uppercase tracking-widest">
                  Fuel Jobber Management System
                </span>
                <Terminal className="w-4 h-4 text-steel" />
              </div>

              <div className="flex items-center justify-center space-x-3">
                <span className="text-text-tertiary text-xs font-mono">SYS-VERSION</span>
                <span className="text-amber text-xs font-mono font-bold">1.0.0</span>
                <span className="text-text-tertiary text-xs">|</span>
                <span className="text-text-tertiary text-xs font-mono">BUILD</span>
                <span className="text-steel text-xs font-mono font-bold">
                  {new Date().getFullYear()}.{String(new Date().getMonth() + 1).padStart(2, '0')}
                </span>
              </div>
            </div>

            {/* System Status Panel */}
            <div className="mb-6 p-4 bg-base-panel border border-panel-border clip-path-octagon relative">
              <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-success-green" />
              <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-success-green" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-success-green" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-success-green" />

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-2 h-2 bg-success-green rounded-full animate-pulse"
                    style={{ boxShadow: '0 0 8px rgba(74,222,128,1), 0 0 12px rgba(74,222,128,0.6)' }}
                  />
                  <span className="text-xs text-success-green font-mono uppercase tracking-wider font-bold">
                    System Online
                  </span>
                </div>
                <Shield className="w-4 h-4 text-steel" />
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-error-red/10 border border-error-red/50 clip-path-octagon relative">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-error-red font-mono uppercase tracking-wider font-bold mb-1">
                      Authentication Failed
                    </p>
                    <p className="text-xs text-text-tertiary font-mono">{error}</p>

                    {showHelp && (
                      <div className="mt-3 pt-3 border-t border-error-red/20 space-y-1.5">
                        <p className="text-[10px] text-text-tertiary font-mono uppercase tracking-wider">
                          Things worth checking
                        </p>
                        <ul className="space-y-1 text-[11px] text-text-tertiary font-mono">
                          <li className="flex space-x-2">
                            <span className="text-amber">&rsaquo;</span>
                            <span>
                              Use the <span className="text-amber">exact email address your
                              invitation was sent to</span> — it is not always the address you use
                              day to day.
                            </span>
                          </li>
                          <li className="flex space-x-2">
                            <span className="text-amber">&rsaquo;</span>
                            <span>
                              If you have not finished enrolling, open your invitation link first.
                              Signing in only works after you have set a password.
                            </span>
                          </li>
                          <li className="flex space-x-2">
                            <span className="text-amber">&rsaquo;</span>
                            <span>
                              If your account was deactivated, an administrator has to re-enable it.
                            </span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                >
                  <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                  <span>User Identification</span>
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input font-mono text-sm"
                    placeholder="user@leviathan.sys"
                  />
                  <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber/40" />
                  <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber/40" />
                  <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber/40" />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber/40" />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                >
                  <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                  <span>Access Code</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input font-mono text-sm tracking-widest"
                    placeholder="••••••••"
                  />
                  <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber/40" />
                  <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber/40" />
                  <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber/40" />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber/40" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full py-3 text-base mt-6 relative group"
              >
                <span className="relative z-10">
                  {isLoading ? 'AUTHENTICATING...' : 'INITIALIZE SESSION'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-panel-border">
              {/* No self-service sign-up: instances are provisioned by the
                  vendor and users are added by admin invitation. */}
              <p className="text-center text-text-tertiary text-xs font-mono">
                Need an account? Ask an administrator on your team to send you an
                invitation.
              </p>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-text-tertiary">QUASAR PRIME LLC</span>
                <span className="text-text-tertiary/50">© {new Date().getFullYear()}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
                <span className="text-text-tertiary/40">ALL RIGHTS RESERVED</span>
                <span className="text-steel">
                  NODE-{Math.floor(Math.random() * 9999).toString().padStart(4, '0')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Technical readout at bottom */}
        <div className="mt-4 flex items-center justify-center space-x-4 text-[10px] font-mono text-text-tertiary">
          <span>SYS.TIME: {new Date().toLocaleTimeString()}</span>
          <span>|</span>
          <span>LATENCY: 12ms</span>
          <span>|</span>
          <span className="text-success-green">SECURE</span>
        </div>
      </div>
    </div>
  );
}
